/**
 * This is loaded by ./preload.js
 */
import 'virtual:uno.css' // eslint-disable-line import/no-unresolved
// import 'virtual:unocss-devtools' // eslint-disable-line import/no-unresolved
import '@unocss/reset/tailwind.css'

import { getEnv, setEnv } from '@monogrid/env'
import { createHead } from '@unhead/vue'
import { gsap } from 'gsap'
import { Draggable } from 'gsap/Draggable'
import { InertiaPlugin } from 'gsap/InertiaPlugin'
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { Cache } from 'three'
import { createApp } from 'vue'

// import VueGtagPlugin from 'vue-gtag'
import base from '../config/base'
import App from './App.vue'
import directives from './directives'
import { useBreakpoint } from './functions/useBreakpoint'
import { routes, setupRouter } from './router'
Cache.enabled = true
gsap.registerPlugin(Draggable, InertiaPlugin, ScrambleTextPlugin, ScrollToPlugin, ScrollTrigger, SplitText)
/**
 * global injection keys
 * we provide types only so they can be referenced
 */
export const globalInjectionKeys = {
  /** @type {import('vue').InjectionKey<import('./Preloader.vue').PreloaderPublicInstance>} */
  preloader: Symbol('preloader'),
  /** @type {typeof import('@monogrid/vue-boost/i18n').getTranslationsInjectionKey} */
  getTranslations: undefined,
  /** @type {typeof import('@monogrid/vue-boost/i18n').i18nInjectionKey} */
  i18n: undefined,
  /** @type {typeof import('@monogrid/vue-boost/cms').CMSAuthInjectionKey} */
  cmsAuth: undefined
}

const boostrap = async () => {
  setEnv(DOT_ENV)
  const env = getEnv()

  const app = createApp(App)

  app.use(createHead()) // https://github.com/vueuse/head

  app.provide(globalInjectionKeys.preloader, window.preloaderInstance) // TODO: Fix this horrible hack

  let routerRoutes = routes
  let addRouterHooks

  // add optional internationalization support
  if (USE_I18N) {
    const { default: translations } = await import('../config/translations/index.mjs')
    const i18nModule = await import('@monogrid/vue-boost/i18n')

    i18nModule.setTranslations(translations)

    app.use(await i18nModule.setupI18n())
    app.provide(i18nModule.i18nInjectionKey, i18nModule.i18n)
    app.provide(i18nModule.getTranslationsInjectionKey, i18nModule.getTranslations)

    routerRoutes = i18nModule.useLocalizedRoutes(routes)
    addRouterHooks = i18nModule.addRouterHooks

    globalInjectionKeys.i18n = i18nModule.i18nInjectionKey
    globalInjectionKeys.getTranslations = i18nModule.getTranslationsInjectionKey
  }

  // add optional CMS support
  if (USE_CMS) {
    const clientModule = await import('@monogrid/vue-boost/urql-client')
    const urlParams = (new URL(document.location.href)).searchParams
    const customExchanges = []
    /** @type {typeof import("@monogrid/vue-boost/cms")} */
    let cmsModule

    if (urlParams.get('m-cms-preview') === '1') {
      cmsModule = await import('@monogrid/vue-boost/cms')
      customExchanges.push(
        cmsModule.cmsAuthExchange,
        cmsModule.cmsContentPreviewExchange
      )
      app.use(cmsModule.CMSAuthPlugin)
      app.provide(cmsModule.CMSAuthInjectionKey, cmsModule.CMSAuth)
      globalInjectionKeys.cmsAuth = cmsModule.CMSAuthInjectionKey
    }

    const { client, urql } = clientModule.provideClient({ customExchanges, endpoint: `${env.CONTENT_API_ENDPOINT}` })
    app.use(urql, client)
    await cmsModule?.CMSAuth.bootstrap({ graphQLClient: app._context.provides.$urql.value })
  }

  // custom directives
  Object.entries(directives).forEach(([key, config]) => app.directive(key, config))

  // https://next.router.vuejs.org/guide/
  const router = setupRouter(routerRoutes, { base, addRouterHooks })

  // https://next.router.vuejs.org/guide/
  app.use(router)

  // https://matteo-gabriele.gitbook.io/vue-gtag/
  // https://matteo-gabriele.gitbook.io/vue-gtag/#initial-config-parameters
  // https://matteo-gabriele.gitbook.io/vue-gtag/auto-tracking#enable-auto-tracking
  // https://matteo-gabriele.gitbook.io/vue-gtag/opt-in-out (see file://./example/examples/ExampleCookieManagement.vue)
  // app.use(VueGtagPlugin, {
  //   config: { id: 'GTM-M99MBN6' },
  //   enabled: false
  // }, router)

  await router.isReady()

  let lastWarning
  app.config.globalProperties.$breakpoints = useBreakpoint()
  app.config.warnHandler = (msg, instance, trace) => {
    if (lastWarning === msg) return
    console.warn('[Vue warn]: '.concat(msg).concat(trace))
    lastWarning = msg
  }

  app.mount('#app')

  return app
}

if (window.app && typeof window.app.unmount === 'function') {
  window.app.unmount()
}

boostrap()
  .then(app => (window.app = app))
  .catch(error => console.error(error))
